// Heuristics and safe translation helpers (no fragile placeholders).

const FR_DIACRITICS_RE = /[ÀÂÄÆÇÉÈÊËÎÏÔŒÙÛÜŸàâäæçéèêëîïôœùûüÿ]/u;

export const hasFrenchDiacritics = (text) => FR_DIACRITICS_RE.test(text || "");

export const isAllCapsWord = (w) => {
  if (!w || typeof w !== "string") return false;
  const letters = w.replace(/[^A-Za-zÀ-ÿ]/g, "");
  return letters.length >= 3 && letters === letters.toUpperCase();
};

// French function words
const FR_FUNCTION_WORDS = new Set([
  "de",
  "du",
  "des",
  "la",
  "le",
  "les",
  "et",
  "au",
  "aux",
  "à",
  "d'",
  "l'",
  "ou",
]);

// Sports domain vocabulary to keep as-is
const DOMAIN_FR = new Set([
  "féminin",
  "masculin",
  "rugby",
  "hockey",
  "soccer",
  "football",
]);

// Acronyms to preserve verbatim
const ACRONYMS = new Set([
  "USPORTS",
  "NCAA",
  "CIS",
  "FIFA",
  "FIBA",
  "UCI",
  "NHL",
  "NFL",
  "MLS",
  "CFL",
]);

const normalize = (s) => (s || "").toLowerCase();

// Detect single “French-ish” tokens (do NOT treat generic ALL-CAPS English as French)
const isFrenchishWord = (w) => {
  if (!w) return false;
  const n = normalize(w);
  return (
    hasFrenchDiacritics(w) ||
    FR_FUNCTION_WORDS.has(n) ||
    DOMAIN_FR.has(n) ||
    ACRONYMS.has(w) // exact-case acronym match
  );
};

export const isLikelyFrench = (text) => {
  if (!text || typeof text !== "string") return false;
  if (hasFrenchDiacritics(text)) return true;
  const words = text.match(/[\p{L}\p{M}]+/gu) || [];
  if (!words.length) return false;
  const frenchish = words.filter(isFrenchishWord).length;
  return frenchish >= Math.ceil(words.length / 2);
};

// Domain cleanup for better UX
const normalizeFrenchDomain = (s) => {
  if (!s) return s;
  // Map “(aux|des)? femmes rugby” → “RUGBY FÉMININ”
  return s.replace(/\b(?:aux|des)?\s*femmes\s+rugby\b/gi, "RUGBY FÉMININ");
};

// Translate to FR while preserving detected-French segments and whitespace.
// - If "Label: VALUE" and VALUE is French-ish, translate only the label.
// - Otherwise, translate only English runs; keep spaces/punct outside the buffer.
export const translatePreservingFrench = async (
  text,
  targetLang,
  translateFn
) => {
  if (!text || typeof text !== "string") return text;
  const target = (targetLang || "").toLowerCase();

  if (target !== "fr") {
    return translateFn(text, target);
  }

  // Handle “Label: VALUE” — translate only label when value is French-ish
  const LABEL_VALUE_RE = /^\s*([^:：]{1,200})(\s*[:：]\s*)(.+)$/s;
  const m = text.match(LABEL_VALUE_RE);
  if (m) {
    const [, left, sep, right] = m;
    const rightTrim = right.trim();
    const words = rightTrim.match(/[\p{L}\p{M}]+/gu) || [];
    const frenchishCount = words.filter(isFrenchishWord).length;
    const hasDiac = words.some(hasFrenchDiacritics);
    const majorityFrenchish =
      words.length > 0 && frenchishCount / words.length >= 0.5;

    if (hasDiac || majorityFrenchish) {
      const translatedLeft =
        (await translateFn(left.trim(), "fr")) || left.trim();
      return `${translatedLeft}${sep}${right}`;
    }
  }

  // Tokenize to preserve whitespace/punctuation exactly
  const TOK_RE = /([\p{L}\p{M}]+(?:['’-][\p{L}\p{M}]+)*)|([^\p{L}\p{M}]+)/gu;

  let out = [];
  let buffer = "";
  let inRun = false;

  const flushBuffer = async () => {
    if (!inRun || !buffer) return;
    try {
      const tr = await translateFn(buffer, "fr");
      out.push(tr ?? buffer);
    } catch {
      out.push(buffer);
    }
    buffer = "";
    inRun = false;
  };

  const tokens = Array.from(text.matchAll(TOK_RE));
  for (const t of tokens) {
    const word = t[1];
    const nonWord = t[2];

    if (word !== undefined) {
      if (isFrenchishWord(word)) {
        // End any English run, keep the French token as-is
        await flushBuffer();
        out.push(word);
      } else {
        // Add this English word to the current run (no spaces here)
        if (!inRun) {
          inRun = true;
          buffer = "";
        }
        buffer += word;
      }
    } else if (nonWord !== undefined) {
      // Always push whitespace/punct as-is; never include it in the translatable buffer
      await flushBuffer();
      out.push(nonWord);
    }
  }
  await flushBuffer();

  return normalizeFrenchDomain(out.join(""));
};
